import React, { useState } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex, Divider,
    Radio, Input, Alert, Breadcrumb, Popconfirm, message, Tooltip
} from "antd";
import {
    BookOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined,
    CloseCircleOutlined, ArrowLeftOutlined, SaveOutlined, DeleteOutlined,
    UserOutlined, ReadOutlined, QuestionCircleOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { JoinRequestDto, RsvpOption } from "../../commons/data/types";
import { createGoogleCalendarUrl } from "../../commons/calendarUtils";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

export const JoinRequestDetailPage: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn>
                <JoinRequestDetailContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

const JoinRequestDetailContent: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data: request, isLoading, isError, refetch } = useQuery<JoinRequestDto>(
        ['join-request-detail', id],
        () => axios.get(`/api/launcher/join-requests/${id}`).then(res => res.data),
        { enabled: !!id }
    );

    const [editing, setEditing] = useState<boolean>(false);
    const [rsvps, setRsvps] = useState<Record<string, RsvpOption>>({});
    const [playerNote, setPlayerNote] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [cancelling, setCancelling] = useState<boolean>(false);

    const sortedSessionRsvps = React.useMemo(() => {
        if (!request?.sessionRsvps) return [];
        return [...request.sessionRsvps].sort((a, b) => {
            if (a.sessionTime && b.sessionTime) {
                return a.sessionTime - b.sessionTime;
            }
            if (a.sessionTime) return -1;
            if (b.sessionTime) return 1;
            return 0;
        });
    }, [request?.sessionRsvps]);

    // Initialize editing state when request is loaded
    React.useEffect(() => {
        if (request) {
            const initialMap: Record<string, RsvpOption> = {};
            (request.sessionRsvps || []).forEach(s => {
                initialMap[s.sessionId] = s.rsvp;
            });
            setRsvps(initialMap);
            setPlayerNote(request.playerNote || '');
        }
    }, [request]);

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (isError || !request) {
        return (
            <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
                <Alert
                    message={t('joinRequests.error.notFoundTitle', 'Join Request Not Found')}
                    description={t('joinRequests.error.notFoundDesc', 'The requested join request could not be found or you do not have permission to view it.')}
                    type="error"
                    showIcon
                    action={
                        <Button type="primary" onClick={() => navigate('/profile')}>
                            {t('profile.backToProfile', 'Back to Profile')}
                        </Button>
                    }
                />
            </div>
        );
    }

    const isPending = request.status === 'PENDING';
    const isApproved = request.status === 'APPROVED';
    const isRejected = request.status === 'REJECTED';
    const isCanceled = request.status === 'CANCELED';
    const isInvitationScheduled = request.invitationStatus === 'SCHEDULED';

    const handleSave = async () => {
        const hasActive = Object.values(rsvps).some(r => r === 'YES' || r === 'MAYBE');
        if (!hasActive) {
            message.warning(t('invitations.rsvp.requireOneActive', 'Please select YES or MAYBE for at least one session.'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                playerNote: playerNote.trim() || undefined,
                sessionRsvps: Object.entries(rsvps).map(([sessionId, rsvp]) => ({
                    sessionId,
                    rsvp
                }))
            };

            await axios.put(`/api/launcher/join-requests/${request.id}/rsvp`, payload);
            message.success(t('joinRequests.success.updated', 'Join request updated successfully!'));
            setEditing(false);
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || t('joinRequests.error.updateFailed', 'Failed to update join request.'));
        } finally {
            setSaving(false);
        }
    };

    const handleCancelRequest = async () => {
        setCancelling(true);
        try {
            await axios.post(`/api/launcher/join-requests/${request.id}/cancel`);
            message.success(t('joinRequests.success.canceled', 'Join request canceled.'));
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || t('joinRequests.error.cancelFailed', 'Failed to cancel join request.'));
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div style={{ maxWidth: '960px', margin: '30px auto', padding: '0 20px' }}>
            {/* BREADCRUMB */}
            <div style={{ marginBottom: 16 }}>
                <Flex justify="space-between" align="center">
                    <Breadcrumb
                        items={[
                            { title: <Link to="/profile">{t('profile.title', 'Profile')}</Link> },
                            { title: <Link to={`/invitations/${request.invitationId}`}>{request.adventureTitle || t('invitations.browse.nav', 'Adventure')}</Link> },
                            { title: t('joinRequests.detail.title', 'Join Request') }
                        ]}
                    />
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/invitations/${request.invitationId}`)}>
                        {t('joinRequests.backToAdventure', 'Back to Adventure')}
                    </Button>
                </Flex>
            </div>

            {/* STATUS BANNER */}
            <Card style={{ ...launcherCard, marginBottom: 24, padding: 0, overflow: 'hidden' }}>
                <div style={{
                    padding: '20px 24px',
                    background: isApproved ? '#f6ffed' : isPending ? '#fffbe6' : isRejected ? '#fff2f0' : '#f5f5f5',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap="small">
                        <Space size="middle">
                            {isApproved && <CheckCircleOutlined style={{ fontSize: 28, color: '#52c41a' }} />}
                            {isPending && <ClockCircleOutlined style={{ fontSize: 28, color: '#faad14' }} />}
                            {isRejected && <CloseCircleOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />}
                            {isCanceled && <CloseCircleOutlined style={{ fontSize: 28, color: '#999' }} />}
                            <div>
                                <Title level={4} style={{ margin: 0 }}>
                                    {isApproved && t('joinRequests.status.approvedTitle', 'Seat Reservation Confirmed!')}
                                    {isPending && t('joinRequests.status.pendingTitle', 'Join Request Pending Approval')}
                                    {isRejected && t('joinRequests.status.rejectedTitle', 'Join Request Declined')}
                                    {isCanceled && t('joinRequests.status.canceledTitle', 'Join Request Canceled')}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    {t('joinRequests.detail.submittedOn', 'Submitted on')}: {dayjs(request.createdAt).format('MMMM D, YYYY [at] HH:mm')}
                                </Text>
                            </div>
                        </Space>

                        <div>
                            {isApproved && <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>{t('joinRequests.status.approved', 'Confirmed')}</Tag>}
                            {isPending && <Tag color="gold" style={{ fontSize: 14, padding: '4px 12px' }}>{t('joinRequests.status.pending', 'Pending')}</Tag>}
                            {isRejected && <Tag color="red" style={{ fontSize: 14, padding: '4px 12px' }}>{t('joinRequests.status.rejected', 'Declined')}</Tag>}
                            {isCanceled && <Tag color="default" style={{ fontSize: 14, padding: '4px 12px' }}>{t('joinRequests.status.canceled', 'Canceled')}</Tag>}
                            {isInvitationScheduled && (
                                <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', marginLeft: 6 }}>
                                    {t('invitations.status.scheduled', 'Scheduled')}
                                </Tag>
                            )}
                        </div>
                    </Flex>
                </div>

                {/* ADVENTURE QUICK OVERVIEW */}
                <div style={{ padding: 24 }}>
                    <Row gutter={[24, 24]} align="middle">
                        {request.adventureCoverUrl && (
                            <Col xs={24} sm={6}>
                                <img
                                    src={request.adventureCoverUrl}
                                    alt={request.adventureTitle || ''}
                                    style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 8 }}
                                />
                            </Col>
                        )}
                        <Col xs={24} sm={request.adventureCoverUrl ? 18 : 24}>
                            <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
                                <Link to={`/invitations/${request.invitationId}`} style={{ color: 'inherit' }}>
                                    {request.adventureTitle}
                                </Link>
                            </Title>
                            <Space wrap size="large" style={{ color: '#666' }}>
                                {request.systemTitle && (
                                    <Flex align="center" gap="small">
                                        <BookOutlined />
                                        <Text strong>{request.systemTitle}</Text>
                                    </Flex>
                                )}
                                {request.organizerName && (
                                    <Flex align="center" gap="small">
                                        <ReadOutlined />
                                        <Text>{request.organizerName}</Text>
                                    </Flex>
                                )}
                                {request.gmName && (
                                    <Flex align="center" gap="small">
                                        <UserOutlined />
                                        <Text>GM: {request.gmName}</Text>
                                    </Flex>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </div>
            </Card>

            {/* SESSIONS & RSVP DETAILS */}
            <Card
                style={{ ...launcherCard, marginBottom: 24 }}
                title={
                    <Flex justify="space-between" align="center">
                        <Title level={4} style={{ margin: 0 }}>
                            <CalendarOutlined /> {t('joinRequests.detail.sessionsAndRsvp', 'Sessions & Availability')}
                        </Title>
                        {isPending && !editing && (
                            <Button type="link" onClick={() => setEditing(true)}>
                                {t('common.edit', 'Edit RSVP')}
                            </Button>
                        )}
                    </Flex>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {sortedSessionRsvps.map((sessionRsvp, idx) => {
                        const currentRsvp = editing
                            ? rsvps[sessionRsvp.sessionId] || sessionRsvp.rsvp
                            : sessionRsvp.rsvp;

                        const calendarUrl = isInvitationScheduled && sessionRsvp.sessionTime
                            ? createGoogleCalendarUrl({
                                title: request.adventureTitle || 'Adventure Session',
                                sessionNumber: idx + 1,
                                sessionTime: sessionRsvp.sessionTime,
                                systemTitle: request.systemTitle,
                                gmName: request.gmName,
                                organizerName: request.organizerName,
                                invitationId: request.invitationId,
                                online: request.online,
                                onlinePlatform: request.onlinePlatform,
                                addressGmapsFormatted: request.addressGmapsFormatted,
                                addressGmapsUrl: request.addressGmapsUrl,
                                accessInfo: request.accessInfo
                            })
                            : null;

                        return (
                            <div
                                key={sessionRsvp.sessionId || idx}
                                style={{
                                    padding: '14px 18px',
                                    background: '#fafafa',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 8,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 12
                                }}
                            >
                                <div>
                                    <Text strong style={{ fontSize: 14 }}>
                                        {t('invitations.detail.sessionNum', 'Session')} #{idx + 1}:{' '}
                                    </Text>
                                    <Text style={{ fontSize: 14 }}>
                                        {sessionRsvp.sessionTime
                                            ? dayjs(sessionRsvp.sessionTime).format('dddd, MMMM D, YYYY [at] HH:mm')
                                            : t('invitations.detail.tbd', 'Date to be announced')}
                                    </Text>
                                </div>

                                {editing ? (
                                    <Radio.Group
                                        value={currentRsvp}
                                        onChange={(e) => setRsvps(prev => ({ ...prev, [sessionRsvp.sessionId]: e.target.value }))}
                                        buttonStyle="solid"
                                        size="middle"
                                    >
                                        <Radio.Button value="YES">
                                            <CheckCircleOutlined /> {t('invitations.rsvp.yes', 'YES')}
                                        </Radio.Button>
                                        <Radio.Button value="MAYBE">
                                            <QuestionCircleOutlined /> {t('invitations.rsvp.maybe', 'MAYBE')}
                                        </Radio.Button>
                                        <Radio.Button value="NO">
                                            <CloseCircleOutlined /> {t('invitations.rsvp.no', 'NO')}
                                        </Radio.Button>
                                    </Radio.Group>
                                ) : (
                                    <Space wrap align="center">
                                        {currentRsvp === 'YES' && (
                                            <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '2px 10px' }}>
                                                {t('invitations.rsvp.yes', 'YES - Available')}
                                            </Tag>
                                        )}
                                        {currentRsvp === 'MAYBE' && (
                                            <Tag color="gold" icon={<QuestionCircleOutlined />} style={{ fontSize: 13, padding: '2px 10px' }}>
                                                {t('invitations.rsvp.maybe', 'MAYBE - Tentative')}
                                            </Tag>
                                        )}
                                        {currentRsvp === 'NO' && (
                                            <Tag color="default" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '2px 10px' }}>
                                                {t('invitations.rsvp.no', 'NO - Unavailable')}
                                            </Tag>
                                        )}
                                        {calendarUrl && ['YES', 'MAYBE'].includes(currentRsvp) && (
                                            <Tooltip title={t('joinRequests.detail.addToCalendarTooltip', 'Add this session to your Google Calendar')}>
                                                <Button
                                                    size="middle"
                                                    icon={<CalendarOutlined />}
                                                    href={calendarUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    type='primary'
                                                >
                                                    {t('joinRequests.detail.addToCalendar', 'Google Calendar')}
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Space>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* NOTES SECTION */}
                <Divider style={{ margin: '20px 0' }} />

                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        {t('joinRequests.detail.yourNote', 'Your Note to the Game Master')}
                    </Text>
                    {editing ? (
                        <TextArea
                            value={playerNote}
                            onChange={(e) => setPlayerNote(e.target.value)}
                            maxLength={150}
                            showCount
                            rows={3}
                            placeholder={t('invitations.rsvp.notesPlaceholder', 'Optional note for the GM...')}
                        />
                    ) : (
                        <Paragraph style={{
                            background: '#fafafa',
                            padding: 12,
                            borderRadius: 6,
                            border: '1px solid #f0f0f0',
                            fontStyle: request.playerNote ? 'normal' : 'italic',
                            color: request.playerNote ? '#333' : '#888',
                            margin: 0
                        }}>
                            {request.playerNote || t('joinRequests.detail.noNoteProvided', 'No note provided.')}
                        </Paragraph>
                    )}
                </div>

                {/* ORGANIZER RESPONSE NOTE (IF AVAILABLE) */}
                {request.organizerResponseNote && (
                    <div style={{ marginTop: 16 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            {t('joinRequests.detail.organizerResponse', 'Organizer / GM Response')}
                        </Text>
                        <Paragraph style={{
                            background: isApproved ? '#f6ffed' : '#fff2f0',
                            padding: 12,
                            borderRadius: 6,
                            border: isApproved ? '1px solid #b7eb8f' : '1px solid #ffccc7',
                            margin: 0
                        }}>
                            {request.organizerResponseNote}
                        </Paragraph>
                    </div>
                )}

                {/* EDIT ACTIONS */}
                {editing && (
                    <Flex justify="flex-end" gap="middle" style={{ marginTop: 20 }}>
                        <Button onClick={() => setEditing(false)} disabled={saving}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSave}
                        >
                            {t('common.save', 'Save Changes')}
                        </Button>
                    </Flex>
                )}

                {/* CANCEL REQUEST ACTION (IF PENDING) */}
                {isPending && !editing && (
                    <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                        <Popconfirm
                            title={t('joinRequests.cancel.confirmTitle', 'Cancel this join request?')}
                            description={t('joinRequests.cancel.confirmDesc', 'Are you sure you want to withdraw your join request for this adventure?')}
                            onConfirm={handleCancelRequest}
                            okText={t('common.yes', 'Yes, Cancel Request')}
                            cancelText={t('common.no', 'No')}
                            okButtonProps={{ danger: true, loading: cancelling }}
                        >
                            <Button danger type="text" icon={<DeleteOutlined />}>
                                {t('joinRequests.cancel.button', 'Cancel / Withdraw Join Request')}
                            </Button>
                        </Popconfirm>
                    </div>
                )}
            </Card>
        </div>
    );
};
